<script lang="ts">
  import type { FormFieldValidationRule } from '../gat-form-field/useFormField'

  export type GatNumEditModelValue = number | undefined

  export interface GatnumEditProps extends GatFormFieldProps<string> {
    /** The value of the input */
    modelValue?: GatNumEditModelValue
    /** The number of decimals to show. If false, no decimals are shown. */
    decimals?: number | false
    /** If the input should be focused when the component is mounted */
    autofocus?: boolean
    /** Input placeholder if the input is empty */
    placeholder?: string
    /** Forces the maximum allowed characters for the input */
    maxLength?: number
  }

  export type GatNumEditEmits = {
    /** Emitted when the input value is changed */
    (e: 'update:modelValue', value: GatNumEditModelValue): void
    /** Emitted when the input loses focus */
    (e: 'blur', event: FocusEvent): void
  }
  // const t is just here to not break the syntax highlighting.
  const t = undefined
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onMounted,
    ref,
    useTemplateRef,
    watch
  } from 'vue'
  import { GatFormField, type GatFormFieldProps } from '../gat-form-field'

  const props = withDefaults(defineProps<GatnumEditProps>(), {
    showOptional: undefined,
    validateOn: 'input',
    hideDetails: 'auto',
    decimals: false,
    autocomplete: 'off'
  })

  const emit = defineEmits<GatNumEditEmits>()

  const loading = defineModel<boolean>('loading', {
    default: false,
    required: false
  })

  const formField = useTemplateRef('form-field')

  const internalValue = ref<string | undefined>(undefined)

  function convertToNumber(v: string | undefined) {
    if (v === undefined || v === '') {
      return undefined
    }
    let result: number | undefined = undefined

    if (typeof v === 'string') {
      // Replace comma with dot for proper number parsing
      const normalizedValue = v.trim().replace(',', '.')
      result = Number(normalizedValue)
      if (props.decimals !== false) {
        result = Number(result.toFixed(props.decimals))
      } else {
        result = Number(result.toFixed(0))
      }
    }

    return result
  }

  watch(internalValue, (v) => {
    let value = convertToNumber(v)
    if (value === undefined) {
      emit('update:modelValue', value)
      return
    }
    if (isNaN(value)) {
      emit('update:modelValue', undefined)
      return
    }
    emit('update:modelValue', value)
  })

  watch(
    () => props.modelValue,
    (value) => {
      if (value !== internalValue.value) {
        internalValue.value = props.modelValue?.toString()
      }
    },
    {
      immediate: true
    }
  )

  const input = useTemplateRef('input')

  function isDecimalsAllowed(value: string | undefined) {
    if (value === '' || value === undefined) {
      return true
    }
    if (props.decimals !== false) {
      return /^[0-9.,]+$/.test(value)
    }
    return /^[0-9]+$/.test(value)
  }

  function isValidNumber(value: string | undefined) {
    if (value === '' || value === undefined) {
      return true
    }
    const newValue = convertToNumber(value)
    if (newValue === undefined) {
      return true
    }
    // if the value is a number, it is valid
    return !isNaN(newValue)
  }

  // async function formatInternalValueOnBlur() {
  //   await nextTick()
  //   const value = internalValue.value
  //   if (value === undefined) {
  //     return
  //   }
  //   if (isValidNumber(value)) {
  //     internalValue.value = convertToNumber(value)?.toString()
  //   }
  // }

  const _rules = computed(() => {
    const result: FormFieldValidationRule<string>[] = []
    if (props.rules) {
      if (Array.isArray(props.rules)) {
        props.rules.forEach((rule) => {
          result.push(rule)
        })
      } else {
        result.push(props.rules)
      }
    }
    result.push((value) => {
      if (!isValidNumber(value)) {
        return `${value} is not a valid number`
      }
      if (!isDecimalsAllowed(value)) {
        return `Decimals are not allowed`
      }
      return true
    })
    return result
  })

  onMounted(() => {
    if (props.autofocus) {
      console.log('autofocus', props.autofocus, props.label)
      focus()
    }
  })

  async function focus() {
    await nextTick()
    if (input.value) {
      setTimeout(() => {
        input.value?.focus?.()
      })
    }
  }

  defineExpose({
    validate: formField.value?.validate(),
    focus,
    loading
  })
</script>

<template>
  <gat-form-field
    ref="form-field"
    v-bind="props"
    :rules="_rules"
    :value="internalValue"
    @clear="emit('update:modelValue', undefined)"
  >
    <template #default="{ errorMessages, isValid, name, disabled, inputClass }">
      <input
        ref="input"
        :id="name"
        v-model="internalValue"
        :data-testid="dataTestid"
        type="text"
        :placeholder
        :autocomplete="autocomplete"
        :disabled="disabled"
        :maxLength
        :class="inputClass"
        @blur="[
          emit('blur', $event),
          formField?.handleBlur($event),
          formField?.setHideDetails(
            props.hideDetails === 'auto' && errorMessages.length === 0
              ? false
              : undefined
          )
          /* formatInternalValueOnBlur() */
        ]"
        @focus="[
          formField?.setHideDetails(
            props.hideDetails === 'auto' ? true : undefined
          )
        ]"
      />
    </template>
    <template #append-inner>
      <slot name="append-inner" />
    </template>
  </gat-form-field>
</template>
